import LibraryRepository, {
  FILE_STATUS_READY,
} from '../../data/repositories/LibraryRepository';
import type {MediaFile} from '../../data/models/LibraryFile/MediaFile';
import {getErrorFromCode} from '../../data/errorCodes';
import {VIDEO_TYPES, videoTypeFor} from './videoSource';
import {createText} from './createText';

export interface UploadedVideo {
  id: string;
  fileName: string;
  fileType?: string;
}

// Clips are cut from the library's copy, which is SD (640x360) unless HD is
// asked for. Every plan allows HD, but at 2.5 Mbps an HD copy of a long video
// is very large, so only videos up to half an hour get one.
const SD = 0;
const HD = 1;
const HD_MAX_SECONDS = 30 * 60;

// The library re-encodes the video before Studio can read it, which takes
// longer the longer the video: about 5 minutes plus a minute for every 4
// minutes of video, at most 45 minutes.
const POLL_MS = 5000;
const BASE_WAIT_MS = 5 * 60 * 1000;
const MAX_WAIT_MS = 45 * 60 * 1000;

/** How long to wait for the library to process a video of this length. */
export function processingWaitMs(seconds: number | null) {
  // A length the browser can't read may be a long video.
  if (seconds === null) return MAX_WAIT_MS;
  return Math.min(MAX_WAIT_MS, BASE_WAIT_MS + (seconds / 4) * 1000);
}

// A WebM is READY at once and replaced later by the library's padded MP4;
// Studio waits for that copy, so the video it measures is the one it cuts.
const processed = (webm: boolean) => (record: MediaFile) =>
  record.fileStatus === FILE_STATUS_READY &&
  (!webm || record.fileType === VIDEO_TYPES.mp4);

/** The video's length in seconds, or null when the browser can't read it. */
export function videoSeconds(file: File): Promise<number | null> {
  return new Promise(resolve => {
    let url: string;
    try {
      url = URL.createObjectURL(file);
    } catch {
      return resolve(null);
    }
    const video = document.createElement('video');
    const done = (seconds: number | null) => {
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve(seconds);
    };
    // Unreadable metadata never blocks the upload; the server checks again.
    const timer = setTimeout(() => done(null), 10000);
    video.preload = 'metadata';
    video.onloadedmetadata = () =>
      done(
        Number.isFinite(video.duration) && video.duration > 0
          ? video.duration
          : null,
      );
    video.onerror = () => done(null);
    video.src = url;
  });
}

/**
 * Uploads a video to the organization's library with the platform's signed
 * upload and resolves once the library has finished processing it, so Studio
 * can transcribe it from there. Rejects with a message for the author.
 */
export async function uploadStudioVideo(
  organizationId: string,
  file: File,
  signal: AbortSignal,
  {
    repository = new LibraryRepository(),
    readSeconds = videoSeconds,
  }: {
    repository?: Pick<LibraryRepository, 'uploadSourceFile'>;
    readSeconds?: (file: File) => Promise<number | null>;
  } = {},
): Promise<UploadedVideo> {
  // Some browsers leave the type blank for .mov or .webm, and the library
  // stores a file as a video only when its type says so.
  const type = videoTypeFor(file);
  const video = type === file.type ? file : new File([file], file.name, {type});
  // The length is not limited; the create page limits the file size.
  const seconds = await readSeconds(video);
  if (signal.aborted) throw new Error(createText.uploadCancelled);
  const isReady = processed(type === VIDEO_TYPES.webm);
  // The library records the length it is told; an unreadable one stays 0.
  const result = await repository.uploadSourceFile(organizationId, video, {
    isCancelled: () => signal.aborted,
    signal,
    duration: seconds ?? 0,
    targetRes: seconds !== null && seconds <= HD_MAX_SECONDS ? HD : SD,
    pollMs: POLL_MS,
    maxPolls: Math.ceil(processingWaitMs(seconds) / POLL_MS),
    isReady,
    // The library removes a video with no sound while processing it.
    failWhenGone: true,
  });
  if (signal.aborted) throw new Error(createText.uploadCancelled);
  const code = result.errors?.[0];
  if (!result.success || !result.record)
    throw new Error(
      code && code !== 'unknown' && code !== 'timeout'
        ? getErrorFromCode(code)
        : createText.uploadFailed,
    );
  if (!isReady(result.record)) throw new Error(createText.stillProcessing);
  const {id, fileName, fileType} = result.record;
  return {id, fileName, fileType};
}
